import { defineStore } from 'pinia';
import { f7 } from 'framework7-vue';
import _ from 'lodash';
import { Browser } from '@capacitor/browser';
import {
  InAppBrowser,
  DefaultSystemBrowserOptions,
} from '@capacitor/inappbrowser';
import { Capacitor } from '@capacitor/core';
import { useKeycloakStore } from './keycloak.js';
import { useLocationStore } from './location.js';

const path = import.meta.env.VITE_APP_URL + 'apimoney';

export const useApimoneyStore = defineStore('apimoney', {
  state: () => ({
    paymentIntent: '',
    bankAccount: '',
    bankAccountList: [],
    standard: '',
    business: '',
    accountEvents: '',
    accountLimits: '',
    accountDocument: '',
    accountDocumentList: [],
    accountFile: '',
    creditCard: '',
    creditCardList: [],
    transaction: '',
    transaction_status: '',
    transactionList: '',
    accounts: '',
    cardInResponse: '',
    bankWire: '',
    baseTransfer: '',
    walletActivityList: [],
    accountWalletList: [],
    groupedWalletList: [],
    userWalletList: [],
    walletActivity: '',
    walletBalance: '',
    apiWallet: '',
    structuredTransactionList: [],
    userPaymentProvider: '',
    paymentTransferMethods: [],
    allowedCountries: [],
    allowedCountryList: [],
  }),
  persist: true,
  actions: {
    setBaseTransfer(payload) {
      this.baseTransfer = payload;
    },
    setTransactionList(payload) {
      console.log('setTransactionList', payload);
      this.structuredTransactionList = payload;
    },
    setUserPaymentProvider(payload) {
      this.userPaymentProvider = payload;
      console.log('setUserPaymentProvider', payload);
    },
    async getPaymentTransferMethods(payload) {
      const { default: axios } = await import('axios');
      console.log('getPaymentTransferMethods', payload);
      axios({
        url: `${path}/payment-methods`,
        method: 'GET',
        params: {
          currencyCode: payload.currencyCode,
          countryCode: payload.countryCode,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((paymentTransferMethods) => {
          console.log('paymentTransferMethods', paymentTransferMethods);
          this.paymentTransferMethods = paymentTransferMethods;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getAllowedCountries() {
      const { default: axios } = await import('axios');
      const locationStore = useLocationStore();

      axios({
        url: `${path}/all-payment-methods`,
        method: 'GET',
      })
        .then((r) => r.data)
        .then((allpaymentTransferMethods) => {
          const getCountryList = () => {
            if (
              Array.isArray(locationStore.country) &&
              locationStore.country.length
            ) {
              return Promise.resolve(locationStore.country);
            }
            return locationStore.getCountry().then(
              () => locationStore.country || []
            );
          };

          getCountryList().then((country) => {
            const allowed = new Set(
              allpaymentTransferMethods.map((m) => m.countryCode)
            );
            const countryList = (country || []).filter((c) =>
              allowed.has(c.countryCode2)
            );
            const sorted = _.sortBy(countryList, [
              'continentName',
              'countryName',
            ]);
            this.allowedCountryList = sorted;
            const grouped = _.groupBy(sorted, 'continentName');
            this.allowedCountries = grouped;
          });
        })
        .catch((err) => {
          console.log(err);
        });
    },

    async addBankAccount(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/bankAccount`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((bankAccount) => {
          console.log(payload);
          console.log(bankAccount);
          this.bankAccount = bankAccount;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getBankAccount(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/bankAccount`,
        method: 'GET',
        params: {
          bankAccountId: payload.bankAccountId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((bankAccount) => {
          console.log(payload);
          console.log(bankAccount);
          this.bankAccount = bankAccount;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async editBankAccount(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/bankAccount`,
        method: 'PUT',
        data: payload.bankAccount,
        params: {
          bankAccountId: payload.bankAccountId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((bankAccount) => {
          console.log(bankAccount);
          this.bankAccount = bankAccount;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async deleteBankAccount(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/bankAccount`,
        method: 'DELETE',
        params: {
          bankAccountId: payload,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((bankAccountList) => {
          console.log(bankAccountList);
          this.bankAccountList = bankAccountList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async getAllBankAccounts(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/bankAccount/list`,
        method: 'GET',
        params: { accountId: payload },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((bankAccountList) => {
          console.log(bankAccountList);
          this.bankAccountList = bankAccountList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    // Credit cards

    async getCreditCard(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/creditCard`,
        method: 'GET',
        params: { creditCardId: payload },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((creditCard) => {
          console.log(payload);
          console.log(creditCard);
          this.creditCard = creditCard;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getAllCreditCards(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/creditCard/list`,
        method: 'GET',
        params: { accountId: payload },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((creditCardList) => {
          console.log(creditCardList);
          this.creditCardList = creditCardList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async editCreditCard(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/creditCard`,
        method: 'PUT',
        data: payload.creditCard,
        params: {
          creditCardId: payload.creditCardId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((creditCard) => {
          console.log(creditCard);
          this.creditCard = creditCard;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async deleteCreditCard(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/creditCard`,
        method: 'DELETE',
        params: {
          creditCardId: payload,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((creditCardList) => {
          console.log(creditCardList);
          this.creditCardList = creditCardList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    // ApiMoneyWallets

    async createApiWallet(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/wallet`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((apiWallet) => {
          console.log(apiWallet);
          this.apiWallet = apiWallet;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getApiWallet(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/wallet`,
        method: 'GET',
        params: {
          walletId: payload.walletId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((apiWallet) => {
          console.log(apiWallet);
          this.apiWallet = apiWallet;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async editApiWallet(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/wallet`,
        method: 'PUT',
        data: payload.apiWallet,
        params: {
          walletId: payload.walletId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((apiWallet) => {
          console.log(apiWallet);
          this.apiWallet = apiWallet;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async deleteApiWallet(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/wallet`,
        method: 'DELETE',
        params: {
          walletId: payload,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((groupedApiWalletList) => {
          console.log(groupedApiWalletList);
          this.groupedWalletList = groupedApiWalletList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async getAllGroupedWallets() {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/wallet/user/grouped`,
        method: 'GET',
        params: { userId: useKeycloakStore().subject },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((groupedWalletList) => {
          console.log(groupedWalletList);
          this.groupedWalletList = groupedWalletList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getAllWallets() {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/wallet/user`,
        method: 'GET',
        params: { userId: useKeycloakStore().subject },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((userWalletList) => {
          console.log(userWalletList);
          this.userWalletList = userWalletList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getAccountWallets(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/wallet/account`,
        method: 'GET',
        params: { accountId: payload },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((accountWalletList) => {
          console.log(accountWalletList);
          this.accountWalletList = accountWalletList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getApiWalletActivity(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/wallet/activity`,
        method: 'GET',
        params: {
          walletId: payload.walletId,
          walletActivityId: payload.walletActivityId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((walletActivity) => {
          console.log(walletActivity);
          this.walletActivity = walletActivity;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async getApiWalletActivities(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/wallet/activities`,
        method: 'GET',
        params: {
          walletId: payload.walletId,
          walletActivityType: payload.walletActivityType,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((walletActivityList) => {
          console.log(walletActivityList);
          this.walletActivityList = walletActivityList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getApiWalletTransactions(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/wallet/transactions`,
        method: 'GET',
        params: {
          walletId: payload.walletId,
          start_date: payload.start_date,
          end_date: payload.end_date,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transactionList) => {
          console.log(transactionList);
          this.transactionList = transactionList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getApiWalletBalance(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/wallet/balance`,
        method: 'GET',
        params: {
          walletId: payload.walletId,
          timeStamp: payload.timeStamp,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((walletBalance) => {
          console.log(walletBalance);
          this.walletBalance = walletBalance;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    // Api accounts

    async createStandardAccount() {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/standard`,
        method: 'POST',
        data: useKeycloakStore().userInfo,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((standard) => {
          console.log(standard);
          this.standard = standard;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async editStandardAccount(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/standard`,
        method: 'PUT',
        data: payload.standard,
        params: { accountId: payload.accountId },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((standard) => {
          console.log(standard);
          this.standard = standard;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async createBusinessAccount(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/business`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((business) => {
          if (business && business.url && business.url !== null) {
            if (Capacitor.isPluginAvailable('Browser')) {
              Browser.open({ url: business.url });
            } else {
              window.location.href = business.url;
            }
          }
          console.log(business);
          this.business = business;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async editBusinessAccount(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/business`,
        method: 'PUT',
        data: payload.business,
        params: { accountId: payload.accountId },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((business) => {
          console.log(business);
          this.business = business;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getAccount(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account`,
        method: 'GET',
        params: {
          accountId: payload.accountId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((account) => {
          console.log(account);
          this.accounts = account;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getAccountEvents(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/events`,
        method: 'GET',
        params: {
          accountId: payload.accountId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((accountEvents) => {
          console.log(accountEvents);
          this.accountEvents = accountEvents;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getAccountLimits(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/limits`,
        method: 'GET',
        params: {
          accountId: payload.accountId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((accountLimits) => {
          console.log(accountLimits);
          this.accountLimits = accountLimits;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async createAccountDocument(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/document`,
        method: 'POST',
        data: payload.formData,
        params: { accountId: payload.accountId },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((accountDocument) => {
          console.log(accountDocument);
          this.accountDocument = accountDocument;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async editAccountDocument(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/document`,
        method: 'PUT',
        data: payload.formData,
        params: { accountId: payload.accountId },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((accountDocument) => {
          console.log(accountDocument);
          this.accountDocument = accountDocument;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async deleteAccountDocument(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/document`,
        method: 'DELETE',
        params: {
          accountId: payload.accountId,
          documentId: payload.documentId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((accountDocumentList) => {
          console.log(accountDocumentList);
          this.accountDocumentList = accountDocumentList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getAccountDocument(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/document`,
        method: 'GET',
        params: {
          accountId: payload.accountId,
          documentId: payload.documentId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((accountDocument) => {
          console.log(accountDocument);
          this.accountDocument = accountDocument;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getAccountDocumentList(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/documents`,
        method: 'GET',
        params: {
          accountId: payload.accountId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((accountDocumentList) => {
          console.log(accountDocumentList);
          this.accountDocumentList = accountDocumentList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async createAccountFile(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/file`,
        method: 'POST',
        data: payload.formData,
        params: {
          accountId: payload.accountId,
          documentId: payload.documentId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((accountFile) => {
          console.log(accountFile);
          this.accountFile = accountFile;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async deleteAccountFile(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/file`,
        method: 'DELETE',
        params: {
          accountId: payload.accountId,
          fileId: payload.fileId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((accountDocumentList) => {
          console.log(accountDocumentList);
          this.accountDocumentList = accountDocumentList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getAccountFile(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/account/file`,
        method: 'GET',
        params: {
          accountId: payload.accountId,
          fileId: payload.fileId,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((accountDocumentList) => {
          console.log(accountDocumentList);
          this.accountDocumentList = accountDocumentList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    // Cash-In transaction

    async authorizeCashInBankwire(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-in/bankwire/authorize`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': `${payload.idempotency_key}`,
        },
      })
        .then((r) => r.data)
        .then((bankWire) => {
          this.bankWire = bankWire;
        })
        .catch((err) => {
          console.log(err);
        });
    },

    async authorizeCashInMobileMoney(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-in/mobile-money/authorize`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((cardInResponse) => {
          this.cardInResponse = cardInResponse;
          console.log('cardInResponse', cardInResponse);
          if (
            cardInResponse &&
            (cardInResponse.payment_url !== null ||
              cardInResponse.redirectUrl !== null)
          ) {
            const paymentUrl =
              cardInResponse.payment_url || cardInResponse.redirectUrl;
            console.log('paymentUrl', paymentUrl);
            if (Capacitor.isPluginAvailable('Browser')) {
              Browser.open({ url: paymentUrl });
            } else {
              window.location.assign(paymentUrl);
            }
          } else if (
            cardInResponse &&
            cardInResponse.user_msisdn !== null &&
            cardInResponse.otp_code !== null
          ) {
            this.transaction_status = cardInResponse;
            f7.views.main.router.navigate('/transaction/sucess', {
              params: {
                bizao_mobile_order_id: cardInResponse['order-id'],
                transactionId: cardInResponse.id,
                props: { transaction_status: cardInResponse },
              },
            });
            console.log('cardInResponse', cardInResponse);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    async initiateCreditCardCashIn(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-in/creditcards/init`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': `${payload.idempotency_key}`,
        },
      })
        .then((r) => r.data)
        .then((cardInResponse) => {
          console.log('cardInResponse', cardInResponse);
          const response = cardInResponse.body
            ? cardInResponse.body
            : cardInResponse;
          console.log('response', response);

          if (
            payload.partner === 'stripe' &&
            response.client_secret !== null &&
            response.id !== null
          ) {
            this.paymentIntent = response;
            console.log('response', response);
          } else if (
            payload.partner === 'bizao' &&
            response &&
            response.payment_url !== null &&
            response.payment_token !== null &&
            response.order_id !== null
          ) {
            console.log('response', response);
            this.cardInResponse = response;

            if (Capacitor.isPluginAvailable('Browser')) {
              Browser.open({ url: response.payment_url });
            } else {
              window.location.assign(response.payment_url);
            }
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    async executeSingleStepCreditCardCashIn(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-in/creditcards/single-step`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
          if (transaction && transaction.client_secret !== null) {
            this.paymentIntent = transaction;
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    async createRegisteredCreditCardCashInAuth(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-in/creditcards/authorize`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
          if (transaction && transaction.client_secret !== null) {
            this.paymentIntent = transaction;
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    async confirmAuthorizedCashIn(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-in/confirm`,
        method: 'PUT',
        params: {
          transactionId: payload,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err);
        });
    },

    async cancelAuthorizedCashIn(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-in/cancel`,
        method: 'DELETE',
        params: {
          transactionId: payload,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err);
        });
    },

    // Transactions

    async getTransaction(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/transaction/detail`,
        method: 'GET',
        params: {
          transactionId: payload,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getPartnerRefTransaction(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/transaction/partner_ref`,
        method: 'GET',
        params: {
          partner_ref: payload,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async getTransactionStatus(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/transaction/status`,
        method: 'GET',
        params: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transaction_status) => {
          this.transaction_status = transaction_status;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async getWalletTransactionList(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/transaction/wallet`,
        method: 'GET',
        params: {
          walletId: payload.walletId,
          start_date: payload.start_date,
          end_date: payload.end_date,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transactionList) => {
          this.transactionList = transactionList;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async refundTransaction(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/transaction/refund`,
        method: 'PUT',
        data: payload,
        params: {
          transactionId: payload.id,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async getTransactionReceipt(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/transaction/receipt`,
        method: 'GET',
        params: {
          transactionId: payload.transactionId,
          lang: payload.lang,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    // Cash-Out transaction

    async authorizeCashOutBankwire(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-ou/bankwire/authorize`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async executeSingleStepCashOut(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-out/bankwire/single-step`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async authorizeCashOutMobileMoney(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-out/mobile-money/authorize`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
          f7.views.main.router.navigate('/transaction/success', {
            params: {
              transactionId: transaction.id,
              props: { transaction: transaction },
            },
          });
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
    async executeCashOutSingleStepMobileMoney(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-out/mobile-money/single-step`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
          route,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async initiateCashOutCreditCardAuthorization(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-out/creditcards/authorize`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async createCashOutCashAuthorization(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-out/cash/authorize`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async createCashOutATMAuthorization(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-out/atm/authorize`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async createCashOutVirtualCardAuthorization(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-out/virtualcard/authorize`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async confirmAuthorizedCashOut(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-out/confirm`,
        method: 'PUT',
        params: {
          transactionId: payload,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async cancelAuthorizedCashOut(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/cash-out/cancel`,
        method: 'DELETE',
        params: {
          transactionId: payload,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    // Transfer

    async createTransferAuthorization(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/transfer/authorize`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
          f7.dialog.alert(err.response.data.message);
        });
    },

    async executeSingleTransfer(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/transfer/single-step`,
        method: 'POST',
        data: payload,
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
          'Idempotency-Key': payload.idempotency_key,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
          f7.dialog.alert(err.response.data.message);
        });
    },

    async confirmTransferAuthorization(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/transfer/confirm`,
        method: 'PUT',
        params: {
          transactionId: payload,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },

    async cancelTransferAuthorization(payload) {
      const { default: axios } = await import('axios');
      axios({
        url: `${path}/transfer/cancel`,
        method: 'DELETE',
        params: {
          transactionId: payload,
        },
        headers: {
          Authorization: `Bearer ${useKeycloakStore().token}`,
        },
      })
        .then((r) => r.data)
        .then((transaction) => {
          this.transaction = transaction;
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    },
  },
});
